import { useMirageAnalyticsContext } from "@mirage/analytics/AnalyticsProvider";
import type { ActionSurfaceComponent } from "@mirage/analytics/events/enums/action_surface_component";
import type { DashActionSurface } from "@mirage/analytics/events/enums/dash_action_surface";
import type { Send_ComposePrompt } from "@mirage/analytics/events/types/send_compose_prompt";
import { PAP_Send_ComposePrompt } from "@mirage/analytics/events/types/send_compose_prompt";
import type { ChatSource } from "@mirage/mosaics/Chat/types";
import type { UserMessageOptions } from "@mirage/mosaics/ConversationAssistant/containers/ConversationChat";
import type { SelectedTemplate } from "@mirage/mosaics/ConversationAssistant/hooks/useConversationDraftConfig";
import type { ComposeParams } from "@mirage/service-dbx-api/service/grpc/context_engine_apiv2/gen/assist_compose_pb";
import { ClientContext } from "@mirage/service-dbx-api/service/grpc/context_engine_apiv2/gen/models_pb";
import { tagged } from "@mirage/service-logging";
import type { PreConfiguredTemplateID } from "@mirage/shared/compose/assist-template";
import {
  getTemplateContent,
  templateIdToTemplateName,
} from "@mirage/shared/compose/assist-template";
import { preConfiguredVoiceIdToDraftName } from "@mirage/shared/compose/compose-voice";
import i18n from "@mirage/translations";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  appendManualMessages,
  postUserMessage,
  updateConversationDraft,
} from "..";
import type { NewUserMessage } from "../types";
import {
  type ConversationRouterState,
  ConversationType,
  CtaSource,
} from "../utils/buildConversationRouterState";
import { convertChatSourceToDocumentInfo } from "../utils/convertChatSourceToDocumentInfo";
import { convertSuggestionSourceToDocumentInfo } from "../utils/convertSuggestionQueryToDocumentInfo";

const logger = tagged("conversation-router-state");

/**
 * Cache to track processed states based on location.key
 * which is unique to every navigation event. This cache
 * ensures that we never process router state more than once.
 */
const processedStates = new Set<string>();

interface UseConversationRouterStateProps {
  agentPayload?: ComposeParams;
  chatSessionId?: string;
  conversationId?: string;
  setSelectedVoiceID: (voiceID: string) => void;
  setSelectedTemplate: (template: SelectedTemplate) => void;
  setUserMessageOptions: (options: UserMessageOptions) => void;
}

const isConversationRouterState = (
  state: unknown,
): state is ConversationRouterState => {
  return (
    state != null && typeof state === "object" && "conversationType" in state
  );
};

const convertAttachments = (sources: ChatSource[]) => {
  return sources.map(convertChatSourceToDocumentInfo);
};

const getPapEventPropsForSummarizeItem = (
  routerState: ConversationRouterState & { chatSources: ChatSource[] },
  chatSessionId: string,
  actionSurfaceComponent: ActionSurfaceComponent,
  dashActionSurface: DashActionSurface,
): Send_ComposePrompt["properties"] => {
  const papEventProps: Send_ComposePrompt["properties"] = {
    actionSurfaceComponent,
    chatSessionId,
    dashActionSurface,
    featureLine: "assist" as const,
    promptType: "summary" as const,
    queryString:
      routerState.ctaSource === CtaSource.StackSummarize
        ? i18n.t("summarize_this_stack")
        : i18n.t("summarize_documents", {
            count: routerState.chatSources.length,
          }),
  };

  // This handle SerpSummarize special case where we might have a search session id or query uuid
  if (routerState.ctaSource === CtaSource.SerpSummarize) {
    const meta = routerState.scenarioMeta;
    if (meta) {
      if ("searchSessionId" in meta && meta.searchSessionId) {
        papEventProps.searchSessionId = meta.searchSessionId;
      }
      if ("searchQueryUuid" in meta && meta.searchQueryUuid) {
        papEventProps.searchQueryUuid = meta.searchQueryUuid;
      }
    }
  }

  return papEventProps;
};

export const useConversationRouterState = ({
  agentPayload,
  chatSessionId,
  conversationId,
  setSelectedVoiceID,
  setSelectedTemplate,
  setUserMessageOptions,
}: UseConversationRouterStateProps) => {
  const { state: routerState, key: locationKey } = useLocation();
  const { reportPapEvent } = useMirageAnalyticsContext();

  useEffect(() => {
    if (
      !routerState ||
      !conversationId ||
      !chatSessionId ||
      !isConversationRouterState(routerState)
    ) {
      return;
    }

    // Skip if already processed
    if (processedStates.has(locationKey)) {
      logger.debug(`Already processed state: ${locationKey}`);
      return;
    }

    // Mark as processed
    processedStates.add(locationKey);

    const { dashActionSurface, actionSurfaceComponent } =
      getDashActionSurfaceComponentLoggingSourceParams(routerState.ctaSource);
    const source = getDashActionSurfaceComponentLoggingSource(
      routerState.ctaSource,
    );

    switch (routerState.conversationType) {
      case ConversationType.SummarizeItem:
        {
          const papEventProps = getPapEventPropsForSummarizeItem(
            routerState as ConversationRouterState & {
              chatSources: ChatSource[];
            },
            chatSessionId,
            actionSurfaceComponent,
            dashActionSurface,
          );

          reportPapEvent(PAP_Send_ComposePrompt(papEventProps));

          postUserMessage({
            agentIds: ["summarize"],
            conversationId,
            message:
              routerState.ctaSource === CtaSource.StackSummarize
                ? i18n.t("summarize_this_stack")
                : i18n.t("summarize_documents", {
                    count: routerState.chatSources.length,
                  }),
            attachments: convertAttachments(routerState.chatSources),
            clientContext:
              routerState.scenarioMeta?.type === "stack"
                ? new ClientContext({
                    clientScenario: "stack",
                    clientScenarioId: routerState.scenarioMeta?.sharingId,
                  })
                : undefined,
            source,
          });
        }
        break;
      case ConversationType.Ask:
        reportPapEvent(
          PAP_Send_ComposePrompt({
            actionSurfaceComponent,
            chatSessionId,
            dashActionSurface,
            featureLine: "assist",
            promptType: "omnibox_query",
            queryString: routerState.message,
            queryLength: routerState.message.length,
          }),
        );

        postUserMessage({
          conversationId,
          message: routerState.message,
          agentPayload: JSON.stringify(agentPayload),
          source,
        });
        break;
      case ConversationType.AskFromSuggestion:
        reportPapEvent(
          PAP_Send_ComposePrompt({
            actionSurfaceComponent,
            chatSessionId,
            dashActionSurface,
            featureLine: "assist",
            promptType: "chat_suggestion",
            queryString: routerState.message,
            queryLength: routerState.message.length,
          }),
        );

        postUserMessage({
          conversationId,
          message: routerState.message,
          agentPayload: JSON.stringify(agentPayload),
          attachments: routerState.attachments.map(
            convertSuggestionSourceToDocumentInfo,
          ),
          agentIds: routerState.agentIds,
          source,
        });
        setUserMessageOptions({
          skipSearch: true,
          agentIds: routerState.agentIds,
        });
        break;
      case ConversationType.WriteWithMessage:
        reportPapEvent(
          PAP_Send_ComposePrompt({
            actionSurfaceComponent,
            chatSessionId,
            dashActionSurface,
            featureLine: "assist",
            promptType: "omnibox_query",
          }),
        );

        postUserMessage({
          conversationId,
          message: routerState.message,
          agentIds: routerState.agentIds,
          agentPayload: JSON.stringify(agentPayload),
          source,
        });
        break;
      case ConversationType.WriteWithDocument:
        reportPapEvent(
          PAP_Send_ComposePrompt({
            actionSurfaceComponent,
            chatSessionId,
            dashActionSurface,
            featureLine: "assist",
            promptType: "omnibox_typeahead",
          }),
        );

        appendManualMessages([
          {
            conversationId,
            message: "",
            agentIds: routerState.agentIds,
            attachments: convertAttachments(routerState.chatSources),
            messageType: "userMessage",
            source,
          },
          {
            conversationId,
            message: i18n.t("compose_assistant_message_partial_match"),
            messageType: "assistantMessage",
            source,
          },
        ]);
        setUserMessageOptions({
          skipSearch: true,
          agentIds: ["write"],
        });
        break;
      case ConversationType.WriteFromTemplate:
        {
          const id =
            routerState.templateId as unknown as PreConfiguredTemplateID;
          const name = templateIdToTemplateName[id];
          const content = getTemplateContent(name);

          reportPapEvent(
            PAP_Send_ComposePrompt({
              actionSurfaceComponent,
              chatSessionId,
              dashActionSurface,
              featureLine: "assist",
              promptType: "omnibox_typeahead",
            }),
          );

          setSelectedTemplate({
            id,
            name,
            content,
          });
          appendManualMessages([
            {
              conversationId,
              message: name,
              agentIds: ["write"],
              agentPayload: JSON.stringify(agentPayload),
              messageType: "userMessage",
              source,
            },
            {
              conversationId,
              message: i18n.t("compose_assistant_message_partial_match"),
              agentIds: ["write"],
              messageType: "assistantMessage",
              source,
            },
          ]);
          setUserMessageOptions({
            skipSearch: true,
            agentIds: ["write"],
          });
        }
        break;
      case ConversationType.WriteFromTone:
        reportPapEvent(
          PAP_Send_ComposePrompt({
            actionSurfaceComponent,
            chatSessionId,
            dashActionSurface,
            featureLine: "assist",
            promptType: "omnibox_query",
          }),
        );

        setSelectedVoiceID(routerState.voiceId);
        appendManualMessages([
          {
            conversationId,
            message:
              preConfiguredVoiceIdToDraftName[
                routerState.voiceId as keyof typeof preConfiguredVoiceIdToDraftName
              ],
            agentIds: routerState.agentIds,
            agentPayload: JSON.stringify(agentPayload),
            messageType: "userMessage",
            source,
          },
          {
            conversationId,
            message: i18n.t("compose_assistant_message_partial_match"),
            agentIds: routerState.agentIds,
            messageType: "assistantMessage",
            source,
          },
        ]);
        setUserMessageOptions({
          agentIds: ["write"],
        });
        break;
      case ConversationType.AskWithMultiDocs:
        // NOTE: We explicitly _do not_ log send.compose_prompt here, because this
        // case does not involve a pre-populated user message. This was an ask from DS.
        appendManualMessages([
          {
            attachments: convertAttachments(routerState.chatSources),
            clientContext:
              routerState.scenarioMeta.type === "stack"
                ? new ClientContext({
                    clientScenario: "stack",
                    clientScenarioId: routerState.scenarioMeta.sharingId,
                  })
                : undefined,
            conversationId,
            message: "",
            messageType: "userMessage",
            source,
          },
          {
            conversationId,
            message: i18n.t("stack_chat_assistant_prompt"),
            messageType: "assistantMessage",
            source,
          },
        ]);
        setUserMessageOptions({
          skipSearch: true,
        });
        break;
      case ConversationType.AskWithPromptMultiDocs:
        reportPapEvent(
          PAP_Send_ComposePrompt({
            actionSurfaceComponent,
            chatSessionId,
            dashActionSurface,
            featureLine: "assist",
            promptType: "chat_suggestion",
            queryString: routerState.message,
            queryLength: routerState.message.length,
          }),
        );

        postUserMessage({
          attachments: convertAttachments(routerState.chatSources),
          clientContext:
            routerState.scenarioMeta.type === "stack"
              ? new ClientContext({
                  clientScenario: "stack",
                  clientScenarioId: routerState.scenarioMeta.sharingId,
                })
              : undefined,
          conversationId,
          message: routerState.message,
          skipSearch: true,
          source,
        });
        break;
      case ConversationType.LegacySession: {
        const messagesToAppend = routerState.messageHistory.map<NewUserMessage>(
          (message) => ({
            conversationId,
            message: message.content,
            attachments:
              message.attachments && message.attachments.length > 0
                ? convertAttachments(message.attachments)
                : undefined,
            messageType:
              message.role === "user" ? "userMessage" : "assistantMessage",
            source,
          }),
        );
        appendManualMessages(messagesToAppend);

        // Set markdown content if available
        if (routerState.markdownContent) {
          updateConversationDraft(conversationId, routerState.markdownContent);
        }
        break;
      }
      default:
        logger.error(
          `No matching handler for router state type: ${(routerState as ConversationRouterState).conversationType}`,
        );
        break;
    }
  }, [
    chatSessionId,
    conversationId,
    routerState,
    agentPayload,
    locationKey,
    setSelectedVoiceID,
    setSelectedTemplate,
    setUserMessageOptions,
  ]);
};

/**
 * The source of the message. This is used to track the source of the message
 * for analytics purposes. Uses the ctaSource enum if the message is triggered
 * from places outside the assistant page, otherwise pass in the actionSurfaceComponent
 * to track the source within the assistant page.
 */
export function getDashActionSurfaceComponentLoggingSource(
  ctaSource: CtaSource | ActionSurfaceComponent,
) {
  const { dashActionSurface, actionSurfaceComponent } =
    getDashActionSurfaceComponentLoggingSourceParams(ctaSource);

  return `${dashActionSurface}-${actionSurfaceComponent}`;
}

export function getDashActionSurfaceComponentLoggingSourceParams(
  ctaSource: CtaSource | ActionSurfaceComponent,
) {
  let dashActionSurface: DashActionSurface;
  let actionSurfaceComponent: ActionSurfaceComponent;
  switch (ctaSource) {
    case CtaSource.OneClickAskPrompt:
      dashActionSurface = "start_page";
      actionSurfaceComponent = "prompt_suggestion";
      break;
    case CtaSource.OmniboxAskQuery:
    case CtaSource.OmniboxAskFromSuggestion:
    case CtaSource.OmniboxDraftWithTemplate:
    case CtaSource.OmniboxDraftWithTone:
    case CtaSource.OmniboxDraftWithDocument:
    case CtaSource.OmniboxDraftWithMessage:
      dashActionSurface = "start_page";
      actionSurfaceComponent = "omnibox";
      break;
    case CtaSource.SerpSummarize:
      dashActionSurface = "serp";
      actionSurfaceComponent = "omnibox";
      break;
    case CtaSource.StackChat:
      dashActionSurface = "stack_details_page";
      actionSurfaceComponent = "stack_header";
      break;
    case CtaSource.StackSuggestedPrompt:
    case CtaSource.StackSummarize:
      dashActionSurface = "stack_details_page";
      actionSurfaceComponent = "prompt_suggestion";
      break;
    case CtaSource.StackItemSummarize:
      dashActionSurface = "stack_details_page";
      actionSurfaceComponent = "stacks";
      break;
    case CtaSource.StartPageStackChat:
      dashActionSurface = "start_page";
      actionSurfaceComponent = "stacks";
      break;
    case CtaSource.FeedItemSummarize:
      dashActionSurface = "start_page";
      actionSurfaceComponent = "activity_feed";
      break;
    case CtaSource.RecentSummarize:
      dashActionSurface = "start_page";
      actionSurfaceComponent = "recents";
      break;
    case CtaSource.LegacySession:
      dashActionSurface = "conversation_page";
      actionSurfaceComponent = "compose_chat_pane";
      break;
    case "compose_editor_pane":
      dashActionSurface = "conversation_page";
      actionSurfaceComponent = "compose_editor_pane";
      break;
    case "compose_chat_pane":
      dashActionSurface = "conversation_page";
      actionSurfaceComponent = "compose_chat_pane";
      break;
    default:
      throw new Error(
        `Unexpected CTA source or action surface component: ${ctaSource}`,
      );
  }
  return { dashActionSurface, actionSurfaceComponent };
}
